import { useState } from "react";
import { supabase } from "@/lib/supabase";
import type { BienPhoto } from "@/types/bien";

const BUCKET = "biens-photos";

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `p-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function extOf(file: File): string {
  const fromName = file.name.split(".").pop()?.toLowerCase();
  if (fromName && /^[a-z0-9]{2,5}$/.test(fromName)) return fromName;
  const map: Record<string, string> = {
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/webp": "webp",
    "image/gif": "gif",
  };
  return map[file.type] ?? "bin";
}

export function useBienPhotoUpload(bienId: string) {
  const [uploading, setUploading] = useState(0);

  async function uploadOne(file: File): Promise<BienPhoto> {
    const id = uuid();
    const path = `${bienId}/${id}.${extOf(file)}`;
    const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
      contentType: file.type,
      cacheControl: "3600",
      upsert: false,
    });
    if (error) throw new Error(`upload(${file.name}): ${error.message}`);
    const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
    return { id, path, url: data.publicUrl };
  }

  async function uploadMany(files: File[]): Promise<BienPhoto[]> {
    setUploading(files.length);
    const out: BienPhoto[] = [];
    try {
      for (const f of files) {
        out.push(await uploadOne(f));
        setUploading((n) => n - 1);
      }
      return out;
    } finally {
      setUploading(0);
    }
  }

  async function removeOne(path: string): Promise<void> {
    const { error } = await supabase.storage.from(BUCKET).remove([path]);
    if (error) throw new Error(`remove(${path}): ${error.message}`);
  }

  return { uploadMany, removeOne, uploading };
}
